import EditIcon from "@mui/icons-material/EditOutlined";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Box, Button, Checkbox, FormControlLabel, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import PortalPageStatusChip from "./PortalPageStatusChip";
import PortalPageStatusSelect from "./PortalPageStatusSelect";
import type { PortalPageStatus } from "./portalPageStatusHelper";

interface Props {
  title: string;
  toggleValue: PortalPageStatus;
  onToggleChange: (value: PortalPageStatus) => void;
  toggleDisabled?: boolean;
  checkboxLabel?: string;
  checkedValue?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  checkboxDisabled?: boolean;
  onEditClick?: () => void;
  showWidgetWarning?: boolean;
}

const PortalPageSettingsEdit = ({
  title,
  toggleValue,
  onToggleChange,
  toggleDisabled,
  checkboxLabel,
  checkedValue,
  onCheckedChange,
  checkboxDisabled,
  onEditClick,
  showWidgetWarning,
  children,
}: PropsWithChildren<Props>) => {
  return (
    <Paper variant="outlined" square={false} sx={{ width: "100%", borderRadius: "8px" }}>
      <Stack spacing={1.25} sx={{ width: "100%", p: 1.5, pr: 1.5, pl: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="subtitle2">{title}</Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {onEditClick && (
              <Tooltip
                title={
                  showWidgetWarning
                    ? "The page will not appear on the Investor Portal until at least one widget has Live status."
                    : false
                }
                arrow
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditIcon />}
                  endIcon={
                    showWidgetWarning ? (
                      <WarningRoundedIcon fontSize="small" sx={{ color: "warning.main" }} />
                    ) : undefined
                  }
                  onClick={onEditClick}
                >
                  Edit
                </Button>
              </Tooltip>
            )}
            <Box sx={{ minWidth: "6.5rem", display: "flex", justifyContent: "flex-end" }}>
              {toggleDisabled ? (
                <PortalPageStatusChip value={toggleValue} />
              ) : (
                <PortalPageStatusSelect value={toggleValue} onChange={onToggleChange} />
              )}
            </Box>
          </Stack>
        </Box>
        {checkedValue !== undefined && onCheckedChange !== undefined && (
          <FormControlLabel
            control={
              <Checkbox
                checked={checkedValue}
                disabled={checkboxDisabled}
                onChange={(_, checked) => onCheckedChange(checked)}
              />
            }
            label={<Typography>{checkboxLabel}</Typography>}
          />
        )}
        {children}
      </Stack>
    </Paper>
  );
};

export default PortalPageSettingsEdit;
